// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { EndpointGroup } from '@api/types';
import type { TagProps } from '@imprivata-cloud/design-system';

export const ASSIGN_CHUNK_SIZE = 50;
export const GROUP_NAME_MAX_LENGTH = 100;

export type GroupRef = Pick<EndpointGroup, 'groupId' | 'displayName'>;

export type GroupFilter = { kind: 'all' } | { kind: 'none' } | { kind: 'group'; groupId: string };

export const ALL_GROUPS: GroupFilter = { kind: 'all' };

export type GroupNameCheck =
  | { kind: 'valid'; displayName: string }
  | { kind: 'empty' }
  | { kind: 'duplicate' };

type TagColor = NonNullable<TagProps['color']>;

// Red (`cabaret`) is left out so a group never reads as an error state.
const TAG_PALETTE: readonly TagColor[] = [
  'flamingo',
  'forest',
  'iris',
  'swagger',
  'pontoon',
  'tangerine',
  'olive',
];

export const chunk = <T>(items: readonly T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, i) =>
    items.slice(i * size, (i + 1) * size),
  );

/**
 * An endpoint is in at most one manual group; synced ids in `groupIds` are not in
 * `manualGroups` and are skipped.
 */
export const resolveManualGroup = (
  groupIds: readonly string[] | undefined,
  manualGroups: ReadonlyMap<string, GroupRef>,
): GroupRef | null =>
  (groupIds ?? []).map(id => manualGroups.get(id)).find(group => group !== undefined) ?? null;

export const matchesGroupFilter = (group: GroupRef | null, filter: GroupFilter): boolean => {
  switch (filter.kind) {
    case 'all':
      return true;
    case 'none':
      return group === null;
    case 'group':
      return group?.groupId === filter.groupId;
  }
};

export const isSameGroupFilter = (a: GroupFilter, b: GroupFilter): boolean =>
  a.kind === 'group' && b.kind === 'group' ? a.groupId === b.groupId : a.kind === b.kind;

/** Hashes the id rather than using list position, so a group keeps its colour when others are deleted. */
export const tagColor = (groupId: string): TagColor => {
  const hash = [...groupId].reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) >>> 0, 0);
  return TAG_PALETTE[hash % TAG_PALETTE.length];
};

/** Mirrors the BFF's create rules; the input's `maxLength` enforces the length limit. */
export const checkGroupName = (input: string, existing: readonly GroupRef[]): GroupNameCheck => {
  const displayName = input.trim();
  if (!displayName) {
    return { kind: 'empty' };
  }
  const folded = displayName.toLocaleLowerCase();
  if (existing.some(group => group.displayName.toLocaleLowerCase() === folded)) {
    return { kind: 'duplicate' };
  }
  return { kind: 'valid', displayName };
};

export const matchesGroupSearch = (group: GroupRef, search: string): boolean =>
  group.displayName.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase());
